import { EmptyState } from '@backstage/core-components';
import {
  Button,
  createStyles,
  Drawer,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Slider,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React from 'react';
import {
  debtOriginOptions,
  debtTriggerOptions,
  debtTypeOptions,
  flowSteps,
  monthlyInterestOptions,
  useCreateTechDebtFlow,
} from './useCreateTechDebtFlow';

const useDrawerStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      width: '42%',
      maxWidth: 720,
      minWidth: 420,
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    body: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      overflowY: 'auto',
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    optionCard: {
      padding: theme.spacing(1.5),
      cursor: 'pointer',
      border: `1px solid ${theme.palette.divider}`,
      height: '100%',
    },
    selectedCard: {
      border: `2px solid ${theme.palette.primary.main}`,
      background: theme.palette.action.hover,
    },
    sectionTitle: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
    helperText: {
      color: theme.palette.text.secondary,
    },
    sliderBlock: {
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
    },
    warningText: {
      color: theme.palette.warning.main,
    },
  }),
);

const costOfDelayScale = {
  businessValue: [
    {
      score: 1,
      header: 'Minor local convenience',
      body: 'Little/no measurable outcome',
    },
    {
      score: 2,
      header: 'Helps one team',
      body: 'Small throughput improvement',
    },
    {
      score: 3,
      header: 'Helps multiple engineers/teams',
      body: 'Clear lead-time/rework reduction',
    },
    {
      score: 4,
      header: 'Materially improves a core system/journey',
      body: 'Major org throughput improvement',
    },
    {
      score: 5,
      header: 'Directly supports top business objective',
      body: 'Unblocks strategic delivery',
    },
  ],
  timeCriticality: [
    {
      score: 1,
      header: 'No deadline',
      body: 'Safe to delay >6 months',
    },
    {
      score: 2,
      header: 'Delay of 1–2 quarters is tolerable',
      body: 'Limited near-term urgency',
    },
    {
      score: 3,
      header: 'Should be addressed this quarter',
      body: 'Interest is compounding',
    },
    {
      score: 4,
      header: 'Needs action in 4–8 weeks',
      body: 'Upcoming release/dependency/EOL pressure',
    },
    {
      score: 5,
      header: 'Hard deadline ≤4 weeks',
      body: 'Rapidly escalating exposure',
    },
  ],
  riskReductionOpportunity: [
    {
      score: 1,
      header: 'Low risk',
      body: 'No meaningful incident/security/compliance exposure',
    },
    {
      score: 2,
      header: 'Minor operational risk',
      body: 'Limited blast radius',
    },
    {
      score: 3,
      header: 'Recurring incidents or blocker',
      body: 'Meaningful impact on roadmap flow',
    },
    {
      score: 4,
      header: 'High risk exposure',
      body: 'Security/compliance concern or major roadmap gating',
    },
    {
      score: 5,
      header: 'Severe risk',
      body: 'Cannot deliver committed strategic initiative without it',
    },
  ],
};

type TechDebtRegistrationDrawerProps = {
  open: boolean;
  context: string;
  initialEntityRef: string;
  onClose: () => void;
};

export const TechDebtRegistrationDrawer = ({
  open,
  context,
  initialEntityRef,
  onClose,
}: TechDebtRegistrationDrawerProps) => {
  const classes = useDrawerStyles();
  const { state, actions } = useCreateTechDebtFlow({
    open,
    initialEntityRef,
    context,
  });

  const {
    screen,
    activeStep,
    nonDebtCode,
    k1Subtype,
    classificationCode,
    classificationReason,
    classificationNextStep,
    title,
    debtTrigger,
    debtType,
    debtOrigin,
    monthlyInterestHours,
    principalHours,
    interestStatement,
    businessValue,
    timeCriticality,
    riskReductionOpportunity,
    isSubmitting,
    submitError,
    createdId,
    canContinueFromDetails,
  } = state;

  const {
    answerQ1,
    answerQ2,
    answerQ3A,
    answerQ3B,
    answerQ4,
    setK1Subtype,
    continueFromNotDebt,
    setTitle,
    setDebtTrigger,
    setDebtType,
    setDebtOrigin,
    setMonthlyInterestHours,
    setPrincipalHours,
    setInterestStatement,
    setBusinessValue,
    setTimeCriticality,
    setRiskReductionOpportunity,
    onBack,
    onNextFromDetails,
    continueFromK1Subtype,
    onSubmit,
  } = actions;

  return (
    <Drawer
      classes={{ paper: classes.paper }}
      anchor="left"
      open={open}
      onClose={onClose}
      onClick={event => event.stopPropagation()}
    >
      <div className={classes.header}>
        <Typography variant="h5">Register Tech Debt</Typography>
        <IconButton title="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <Stepper activeStep={Math.min(activeStep, flowSteps.length - 1)} alternativeLabel>
        {flowSteps.map(step => (
          <Step key={step}>
            <StepLabel>{step}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <div className={classes.body}>
        {screen === 'Q1' && (
          <>
            <Typography variant="h6">Step 1: Executable/Data Check</Typography>
            <Typography variant="body2">
              Does fixing this require changing code, tests, build/deploy scripts,
              infrastructure-as-code, or data structures/models?
            </Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Button variant="contained" color="primary" onClick={() => answerQ1(true)}>
                  Yes
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" color="primary" onClick={() => answerQ1(false)}>
                  No
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {screen === 'Q2' && (
          <>
            <Typography variant="h6">Step 2: Defect vs Improvement</Typography>
            <Typography variant="body2">
              Is this primarily a defect (something wrong/broken) or a system improvement?
            </Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => answerQ2('defect')}
                >
                  Defect
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => answerQ2('improvement')}
                >
                  System Improvement
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {screen === 'Q3A' && (
          <>
            <Typography variant="h6">Step 3A: Normal Bug Check</Typography>
            <Typography variant="body2">
              Is this a normal incorrect-functionality bug where the system simply does not do
              what it should?
            </Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Button variant="contained" color="primary" onClick={() => answerQ3A(true)}>
                  Yes, normal bug
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" color="primary" onClick={() => answerQ3A(false)}>
                  No, deeper design issue
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {screen === 'Q3B' && (
          <>
            <Typography variant="h6">Step 3B: Improvement Type</Typography>
            <Typography variant="body2">
              Is this mainly a design limitation that keeps slowing delivery, or generic new
              feature work?
            </Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Button variant="contained" color="primary" onClick={() => answerQ3B(true)}>
                  Design limitation
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" color="primary" onClick={() => answerQ3B(false)}>
                  Generic new feature
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {screen === 'Q4' && (
          <>
            <Typography variant="h6">Step 4: Accumulation Test</Typography>
            <Typography variant="body2">
              If left unresolved, does this keep increasing cost/risk/effort over time (rework,
              slower delivery, repeated incidents, risk, duplication, workaround patches)?
            </Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Button variant="contained" color="primary" onClick={() => answerQ4(true)}>
                  Yes, accumulates
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" color="primary" onClick={() => answerQ4(false)}>
                  No
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {screen === 'NOT_DEBT' && (
          <>
            <Typography variant="h6">Not Classified as Tech Debt ({nonDebtCode})</Typography>
            <Typography variant="body2">{classificationReason}</Typography>
            <Typography variant="body2">{classificationNextStep}</Typography>
            <Typography variant="body2">
              You can close and register this in Jira as normal bug/feature/planning work, or
              continue anyway.
            </Typography>
          </>
        )}

        {screen === 'K1_SUBTYPE' && (
          <>
            <Typography variant="h6">Classified as Tech Debt (K1)</Typography>
            <Typography variant="body2">
              Pick the subtype to make tracking and prioritization clearer.
            </Typography>
            <Grid container spacing={1}>
              <Grid item>
                <Button
                  variant={k1Subtype === 'K1a' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setK1Subtype('K1a')}
                >
                  K1a: Design issue / codebase mess
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant={k1Subtype === 'K1b' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setK1Subtype('K1b')}
                >
                  K1b: Design limitation / architecture constraint
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {screen === 'DETAILS' && (
          <>
            <Typography variant="h6">Details for triage and prioritization</Typography>
            <Typography variant="body2" className={classes.helperText}>
              Core selections are required for triage. It is a great help if you fill in as much
              context as possible; free-text explanations are helpful but optional.
            </Typography>
            <TextField
              label="Tech Debt Title"
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Example: Upgrade catalog ingestion pipeline"
              required
              fullWidth
            />

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Debt trigger — what led up to this?
            </Typography>
            <Grid container spacing={1}>
              {debtTriggerOptions.map(option => {
                const selected = debtTrigger === option.value;
                return (
                  <Grid item xs={12} sm={6} key={option.value}>
                    <Paper
                      className={`${classes.optionCard} ${selected ? classes.selectedCard : ''}`}
                      onClick={() => setDebtTrigger(option.value)}
                      role="button"
                      elevation={0}
                    >
                      <Typography variant="subtitle2">{option.label}</Typography>
                      <Typography variant="body2" className={classes.helperText}>
                        {option.description}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Debt type
            </Typography>
            <Typography variant="body2" className={classes.helperText}>
              Pre-selected where possible from classifier answers. Adjust if needed.
            </Typography>
            <Grid container spacing={1}>
              {debtTypeOptions.map(option => {
                const selected = debtType === option.value;
                return (
                  <Grid item xs={12} sm={6} key={option.value}>
                    <Paper
                      className={`${classes.optionCard} ${selected ? classes.selectedCard : ''}`}
                      onClick={() => setDebtType(option.value)}
                      role="button"
                      elevation={0}
                    >
                      <Typography variant="subtitle2">{option.label}</Typography>
                      <Typography variant="body2" className={classes.helperText}>
                        {option.description}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Origin — how did it happen?
            </Typography>
            <Grid container spacing={1}>
              {debtOriginOptions.map(option => {
                const selected = debtOrigin === option.value;
                return (
                  <Grid item xs={12} sm={4} key={option.value}>
                    <Paper
                      className={`${classes.optionCard} ${selected ? classes.selectedCard : ''}`}
                      onClick={() => setDebtOrigin(option.value)}
                      role="button"
                      elevation={0}
                    >
                      <Typography variant="subtitle2">{option.label}</Typography>
                      <Typography variant="body2" className={classes.helperText}>
                        {option.description}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Economic impact
            </Typography>

            <div className={classes.sliderBlock}>
              <Typography variant="subtitle2">Monthly interest (hours/month)</Typography>
              <Slider
                value={monthlyInterestOptions.indexOf(monthlyInterestHours ?? 0)}
                onChange={(_, value) => {
                  const index = Array.isArray(value) ? value[0] : value;
                  setMonthlyInterestHours(monthlyInterestOptions[index]);
                }}
                min={0}
                max={monthlyInterestOptions.length - 1}
                step={1}
                marks={monthlyInterestOptions.map((hours, index) => ({
                  value: index,
                  label: String(hours),
                }))}
                valueLabelDisplay="auto"
                valueLabelFormat={index => `${monthlyInterestOptions[index as number]}h`}
              />
              <Typography variant="body2" className={classes.helperText}>
                Selected: {monthlyInterestHours ?? 0} hours/month
              </Typography>
              {monthlyInterestHours === 40 && (
                <Typography variant="body2" className={classes.warningText}>
                  If this exceeds 40h/month, consider breaking the work into smaller debt items.
                </Typography>
              )}
            </div>

            <div className={classes.sliderBlock}>
              <Typography variant="subtitle2">Principal (remediation effort, hours)</Typography>
              <Slider
                value={principalHours ?? 0}
                onChange={(_, value) =>
                  setPrincipalHours(Array.isArray(value) ? value[0] : value)
                }
                min={0}
                max={500}
                step={10}
                marks={[0, 100, 200, 300, 400, 500].map(value => ({
                  value,
                  label: String(value),
                }))}
                valueLabelDisplay="auto"
              />
              <Typography variant="body2" className={classes.helperText}>
                Selected: {principalHours ?? 0} hours
              </Typography>
              {principalHours === 500 && (
                <Typography variant="body2" className={classes.warningText}>
                  Larger initiatives should be broken down into smaller, trackable debt items.
                </Typography>
              )}
            </div>

            <TextField
              label="Interest statement (optional): What pain/risk continues if not fixed?"
              value={interestStatement}
              onChange={event => setInterestStatement(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />

            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Cost of Delay
            </Typography>

            <Typography variant="subtitle2">Business Value (1–5)</Typography>
            <Grid container spacing={1}>
              {costOfDelayScale.businessValue.map(option => {
                const selected = businessValue === option.score;
                return (
                  <Grid item xs={12} sm={6} key={`bv-${option.score}`}>
                    <Paper
                      className={`${classes.optionCard} ${selected ? classes.selectedCard : ''}`}
                      onClick={() => setBusinessValue(option.score)}
                      role="button"
                      elevation={0}
                    >
                      <Typography variant="subtitle2">{option.header}</Typography>
                      <Typography variant="body2" className={classes.helperText}>
                        {option.body}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
            <Typography variant="body2" className={classes.helperText}>
              Quick test: If fixed next month, would anyone outside the team notice? No → 1–2,
              org-wide improvement → 3, strategic/exec-level outcome → 4–5.
            </Typography>

            <Typography variant="subtitle2">Time Criticality (1–5)</Typography>
            <Grid container spacing={1}>
              {costOfDelayScale.timeCriticality.map(option => {
                const selected = timeCriticality === option.score;
                return (
                  <Grid item xs={12} sm={6} key={`tc-${option.score}`}>
                    <Paper
                      className={`${classes.optionCard} ${selected ? classes.selectedCard : ''}`}
                      onClick={() => setTimeCriticality(option.score)}
                      role="button"
                      elevation={0}
                    >
                      <Typography variant="subtitle2">{option.header}</Typography>
                      <Typography variant="body2" className={classes.helperText}>
                        {option.body}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
            <Typography variant="body2" className={classes.helperText}>
              Quick test: Does value/risk change materially with time? Not really → 1–2,
              compounds → 3–4, hard deadline/rapid worsening → 5.
            </Typography>

            <Typography variant="subtitle2">
              Risk Reduction / Opportunity Enablement (1–5)
            </Typography>
            <Grid container spacing={1}>
              {costOfDelayScale.riskReductionOpportunity.map(option => {
                const selected = riskReductionOpportunity === option.score;
                return (
                  <Grid item xs={12} sm={6} key={`rr-${option.score}`}>
                    <Paper
                      className={`${classes.optionCard} ${selected ? classes.selectedCard : ''}`}
                      onClick={() => setRiskReductionOpportunity(option.score)}
                      role="button"
                      elevation={0}
                    >
                      <Typography variant="subtitle2">{option.header}</Typography>
                      <Typography variant="body2" className={classes.helperText}>
                        {option.body}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {screen === 'REVIEW' && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Scope</Typography>
              <Typography variant="body2">{context}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Title</Typography>
              <Typography variant="body2">{title}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Debt Trigger</Typography>
              <Typography variant="body2">
                {debtTriggerOptions.find(option => option.value === debtTrigger)?.label || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Debt Type</Typography>
              <Typography variant="body2">
                {debtTypeOptions.find(option => option.value === debtType)?.label || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Origin</Typography>
              <Typography variant="body2">
                {debtOriginOptions.find(option => option.value === debtOrigin)?.label || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Monthly Interest</Typography>
              <Typography variant="body2">{monthlyInterestHours ?? 0} hours/month</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Principal</Typography>
              <Typography variant="body2">{principalHours ?? 0} hours</Typography>
            </Grid>
            {interestStatement?.trim() && (
              <Grid item xs={12}>
                <Typography variant="subtitle2">Interest Statement</Typography>
                <Typography variant="body2">{interestStatement}</Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2">Business Value / Time Criticality / RR-OE</Typography>
              <Typography variant="body2">
                {businessValue ?? '-'} / {timeCriticality ?? '-'} /{' '}
                {riskReductionOpportunity ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Classification</Typography>
              <Typography variant="body2">
                {classificationCode}
                {k1Subtype ? ` (${k1Subtype})` : ''}
              </Typography>
              <Typography variant="body2">{classificationReason}</Typography>
            </Grid>
            {submitError && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">
                  {submitError}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        {screen === 'SUCCESS' && (
          <EmptyState
            missing="data"
            title="Tech debt registered"
            description={`Created entry${createdId ? ` with key ${createdId}` : ''}.`}
          />
        )}
      </div>

      <div className={classes.footer}>
        <Button
          variant="outlined"
          color="primary"
          disabled={screen === 'Q1' || screen === 'SUCCESS' || isSubmitting}
          onClick={onBack}
        >
          Back
        </Button>

        {screen === 'NOT_DEBT' && (
          <Button variant="contained" color="primary" onClick={continueFromNotDebt}>
            Continue anyway
          </Button>
        )}

        {screen === 'K1_SUBTYPE' && (
          <Button
            variant="contained"
            color="primary"
            disabled={!k1Subtype}
            onClick={continueFromK1Subtype}
          >
            Continue
          </Button>
        )}

        {screen === 'DETAILS' && (
          <Button
            variant="contained"
            color="primary"
            disabled={!canContinueFromDetails}
            onClick={onNextFromDetails}
          >
            Next
          </Button>
        )}

        {screen === 'REVIEW' && (
          <Button
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        )}

        {screen === 'SUCCESS' && (
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
        )}

        {screen === 'NOT_DEBT' && (
          <Button variant="outlined" color="primary" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    </Drawer>
  );
};